/**
 * ClientDecisionsTabPage
 *
 * Dedicated, full-width "Decyzje klienta" offer workspace tab. Promotes
 * UnitDecisionsSection (previously cramped inside a ~600px version card on
 * the Versions tab — see version-history-tab.tsx) to its own top-level tab
 * so the table's right-hand columns (accepted dates, price, reservation)
 * have room to render. Adds a version selector on top, defaulting to the
 * offer's currently accepted version (falling back to the most recent
 * version the client has otherwise acted on, i.e. rejectedAt != null).
 *
 * Reuses UnitDecisionsSection verbatim — this component only owns version
 * selection and the client-acted-versions empty state.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledSelectField } from '@ooh/ui/components/labeled-select-field';
import { offerVersionEndpoints } from '@ooh/contracts';
import type { OfferVersion } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { useHasPermission } from '../../plugs/current-user';
import { offerVersionsRequest } from './api/queries';
import { UnitDecisionsSection } from './unit-decisions-section';
import { isClientActedVersion, pickDefaultVersionId } from './lib/offer-decision-version';
import type { SortDir, SortKey } from './unit-decisions/filters';

// ─── Version selection ──────────────────────────────────────────

/**
 * A version the client has acted on — either accepted it or rejected it.
 * Exported for version-history-tab.tsx's VersionRow, which gates its
 * client-decision summary on the same condition (CU-86caypxdz).
 */
/**
 * Defaults to the offer's currently ACCEPTED version; when none is accepted
 * (e.g. all client-acted versions were rejected), falls back to the most
 * recent client-acted version; when the client has not acted on anything yet,
 * falls back to the latest version (the same one getSelectableVersions()
 * offers in that case) so the selector never shows an option with nothing
 * selected. `versions` is in the list endpoint's ascending (oldest-first)
 * order, so the last entry is the most recent one.
 */
/**
 * Versions selectable in the "Decyzje klienta" tab: every client-acted version,
 * plus the latest published version even when the client has not acted on it yet.
 *
 * Without the latest-version fallback, a salesperson publishing v2/v3 without the
 * client re-visiting the shared link leaves v1 (still ACCEPTED — a newer version's
 * status only flips a version to SUPERSEDED when THAT newer version is itself
 * accepted, not merely published) as the only client-acted version. Staff then has
 * no way to select the live version to see its (all-pending) decision state or its
 * own unit count — the selector looks permanently "stuck" on a stale version.
 * `versions` is ascending (oldest-first); the last entry is the latest.
 * CU-86caypxe7.
 */
export function getSelectableVersions(versions: OfferVersion[]): OfferVersion[] {
  const latest = versions[versions.length - 1];
  if (latest == null) return [];
  const clientActed = versions.filter(isClientActedVersion);
  if (clientActed.some((version) => version.id === latest.id)) return clientActed;
  return [...clientActed, latest];
}

// ─── Main component ─────────────────────────────────────────────

export interface ClientDecisionsTabPageProps {
  offerId: string;
  canEdit: boolean;
  canViewSupplierDetails?: boolean;
  sortKey?: SortKey;
  sortDir?: SortDir;
  onSortChange?: (sortKey: SortKey, sortDir: SortDir) => void;
}

function resolveTableState(props: ClientDecisionsTabPageProps) {
  return {
    canViewSupplierDetails: props.canViewSupplierDetails ?? true,
    sortKey: props.sortKey ?? ('decision' as const),
    sortDir: props.sortDir ?? ('asc' as const),
    onSortChange: props.onSortChange,
  };
}

export function ClientDecisionsTabPage(props: ClientDecisionsTabPageProps) {
  return (
    <QueryBoundary fallback={<SectionSkeleton />}>
      <ClientDecisionsTabContent {...props} />
    </QueryBoundary>
  );
}

function ClientDecisionsTabContent(props: ClientDecisionsTabPageProps) {
  const { offerId, canEdit } = props;
  const { canViewSupplierDetails, sortKey, sortDir, onSortChange } = resolveTableState(props);
  const { t } = useTranslation('offers');
  const { data } = useApiSuspenseQuery(offerVersionEndpoints.list, offerVersionsRequest(offerId));
  const canManageReservation = useHasPermission('reservation.manage');
  const canManageRemoval = canEdit && canManageReservation;

  const [pickedVersionId, setSelectedVersionId] = React.useState<number | null>(null);

  const versions = data.versions;
  const selectableVersions = React.useMemo(() => getSelectableVersions(versions), [versions]);
  const selectedVersionId = pickedVersionId ?? pickDefaultVersionId(versions);

  if (selectableVersions.length === 0) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center px-8 py-24 text-center"
        data-testid="client-decisions-tab-empty"
      >
        <p className="max-w-md text-sm text-muted-foreground">
          {t('workspace.decisions.emptyNoVersions')}
        </p>
      </div>
    );
  }

  const versionOptions = [...selectableVersions].reverse().map((version) => ({
    value: String(version.id),
    label: t('workspace.decisions.versionOptionLabel', {
      number: version.versionNumber,
      // Order matters: a version can be both rejectedAt-set AND not ACCEPTED, but
      // never ACCEPTED and rejected at once — check ACCEPTED first, then rejection,
      // then fall back to "still live, not yet decided" (the latest-version case
      // getSelectableVersions adds even when isClientActedVersion is false).
      status:
        version.status === 'ACCEPTED'
          ? t('workspace.versions.statusLabel.ACCEPTED')
          : version.rejectedAt != null
            ? t('workspace.versions.rejectedBadge')
            : t('workspace.versions.statusLabel.PUBLISHED'),
    }),
  }));

  return (
    <div data-testid="client-decisions-tab">
      {/* Sticky, pinned below the workspace tabs bar (top-11 == its h-11 in
          workspace-shell.tsx) so scrolling over the content below scrolls the
          page instead of a nested scroll box. */}
      <div className="sticky top-11 z-10 border-b border-border bg-background px-6 py-3">
        <div className="w-64">
          <LabeledSelectField
            id="client-decisions-version-select"
            label={t('workspace.decisions.versionSelectLabel')}
            options={versionOptions}
            value={selectedVersionId != null ? String(selectedVersionId) : ''}
            onChange={(value) => setSelectedVersionId(Number(value))}
            testId="client-decisions-version-select"
          />
        </div>
      </div>

      <div className="px-6 py-5">
        {selectedVersionId != null ? (
          <UnitDecisionsSection
            offerId={offerId}
            versionId={selectedVersionId}
            canManageRemoval={canManageRemoval}
            canViewSupplierDetails={canViewSupplierDetails}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        ) : null}
      </div>
    </div>
  );
}
